import React, { FC, ReactNode } from "react"
import { IconAccount, SkipNavigationLink } from "dash-styled"

import { useBoolean } from "../../../Hooks/useBoolean"
import { APIAppInstanceModel } from "../../../Models/API/AppInstanceModel"
import { FetchState, hasValue } from "../../../Models/Client/FetchState"
import { AccountMenu } from "../../Views/Dashboard/Sections/DashboardBase/AccountMenu"
import { OutlinedBackButton } from "../Button/OutlinedBackButton"

interface BaseLayoutProps {
    orgUsername: string
    instanceFetch: FetchState<APIAppInstanceModel>
    toolbar: ReactNode
    onBack?: () => void
    footer?: ReactNode
}

interface EditJobOutputLayoutProps extends BaseLayoutProps {
    mode: "edit_job"
    jobID: string
    title: string
}

interface EditGroundTruthLayoutProps extends BaseLayoutProps {
    mode: "edit_groundtruth"
    groundTruthID: string
}

interface CreateGroundTruthLayoutProps extends BaseLayoutProps {
    mode: "create_groundtruth"
}

interface TaskLayoutProps extends BaseLayoutProps {
    mode: "task"
    incompleteJobs?: number
}

interface OnboardingLabelingProps extends BaseLayoutProps {
    mode: "onboarding_labeling"
    title: string
}

export type LabelingLayoutProps =
    | TaskLayoutProps
    | EditJobOutputLayoutProps
    | EditGroundTruthLayoutProps
    | CreateGroundTruthLayoutProps
    | OnboardingLabelingProps

export const LabelingLayout: FC<LabelingLayoutProps> = props => {
    const { children, instanceFetch, toolbar, onBack, orgUsername, footer } =
        props
    const [accountMenuShown, showAccountMenu, hideAccountMenu] =
        useBoolean(false)

    const instance = hasValue(instanceFetch) ? instanceFetch.value : undefined

    return (
        <div className="flex flex-col items-stretch justify-between h-full bg-grape-900">
            <SkipNavigationLink />
            <div className="flex justify-between px-4 h-16 items-center z-50 text-sm">
                <div className="flex items-center flex-grow justify-between">
                    {(instance !== undefined ||
                        props.mode === "onboarding_labeling") && (
                        <>
                            {onBack !== undefined && (
                                <OutlinedBackButton
                                    testId="back-btn"
                                    onClick={onBack}
                                />
                            )}
                            {props.mode === "edit_job" ? (
                                <h1 className="ml-3 m-0 flex items-center font-semibold opacity-60">
                                    <span className="text-xl text-white">
                                        {props.title}
                                    </span>
                                </h1>
                            ) : (
                                <h1 className="ml-3 m-0 flex items-center font-semibold opacity-60">
                                    <span className="text-xl text-white">
                                        {props.mode === "create_groundtruth"
                                            ? "New ground-truth data"
                                            : props.mode === "edit_groundtruth"
                                            ? `Ground-truth data ${props.groundTruthID}`
                                            : props.mode ===
                                              "onboarding_labeling"
                                            ? instance?.name ?? props.title
                                            : instance?.name}
                                    </span>

                                    {props.mode === "task" &&
                                        props.incompleteJobs !== undefined && (
                                            <span className="ml-4 p-1 text-sm leading-none rounded bg-rhino-300">
                                                {`${
                                                    props.incompleteJobs > -1
                                                        ? props.incompleteJobs
                                                        : "Calculating"
                                                } remaining`}
                                            </span>
                                        )}
                                </h1>
                            )}
                            <span></span>
                        </>
                    )}
                </div>
                {toolbar}
                <button
                    className="text-smoke-850 py-2 m-2 mr-0 ml-8 relative"
                    onMouseMove={showAccountMenu}
                    onMouseLeave={hideAccountMenu}
                    onClick={showAccountMenu}
                >
                    <IconAccount size="xxl" />
                    {accountMenuShown ? (
                        <AccountMenu
                            orgUsername={orgUsername}
                            compact={true}
                            className="absolute right-0 text-black top-full"
                        />
                    ) : null}
                </button>
            </div>
            <div
                id="maincontent"
                className="rounded-t-lg bg-white h-0 flex-grow flex-shrink"
            >
                {children}
            </div>
            {footer}
        </div>
    )
}
